import { Alert, Button, Intent, Position, Toaster } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    GetSpeculativeProjectsDocument,
    useDeleteSpeculativeProjectMutation
} from "generated/graphql";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { segmentTrackDeleteSpeculativeProject } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useSubscriptions } from "in_queue/contexts/InQueueSubscriptionsContext";
import { SpeculativeProject } from "in_queue/types/projectType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ADMIN_TYPE } from "types/roleType";

export const DeleteSpeculativeProjectButton: React.FC<{
    project: SpeculativeProject;
    scenarios: ReadonlyArray<ScenarioMetadata>;
}> = ({ project, scenarios }) => {
    const cluster = useCluster();
    const navigate = useNavigate();
    const { role } = useUserDataContext();
    const isNotAdmin = role !== ADMIN_TYPE;
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const subscriptions = useSubscriptions();

    const [deleteSpeculativeProject, deleteResult] =
        useDeleteSpeculativeProjectMutation();

    // Don't allow deleting a study project if the user has used it in a scenario.
    // This would break data the scenario needs to render, and it would also
    // allow a customer to run scenarios for more study projects than their
    // allotted limit.
    if (isNotAdmin || scenarios.length > 0) {
        return <></>;
    }

    const onConfirmDelete = async () => {
        if (subscriptions === "loading") {
            throw new Error("Unable to delete speculative project");
        }

        const subscriptionIds = subscriptions.map((sub) => sub.subscriptionId);
        try {
            await deleteSpeculativeProject({
                variables: {
                    projectId: project.projectId
                },
                refetchQueries: [
                    {
                        query: GetSpeculativeProjectsDocument,
                        variables: {
                            subscriptionIds
                        }
                    }
                ]
            });
            segmentTrackDeleteSpeculativeProject({
                projectId: project.projectId
            });
            navigate(`/${IN_QUEUE_PATH}/${cluster.slug}`);
        } catch (error) {
            DeleteStudyProjectToaster.show({
                intent: Intent.DANGER,
                message:
                    "Encountered an error while deleting the study project. Please try again later."
            });
        }
        setIsAlertOpen(false);
    };

    return (
        <>
            <Button
                icon={IconNames.TRASH}
                text="Delete project"
                onClick={() => setIsAlertOpen(true)}
            />
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Delete project"
                icon="trash"
                intent={Intent.DANGER}
                isOpen={isAlertOpen}
                loading={deleteResult.loading}
                onCancel={() => setIsAlertOpen(false)}
                onConfirm={onConfirmDelete}
            >
                <p>
                    Are you sure you want to delete this study project? This
                    action cannot be undone.
                </p>
            </Alert>
        </>
    );
};

const DeleteStudyProjectToaster = Toaster.create({
    position: Position.TOP
});
